import { lazy } from 'react';

/*
 * Lazy routes that survive a deploy, ported from hr-portal-frontend
 * (src/utils/lazyLoad.js) where route-level splitting has been load-bearing since
 * July 2026.
 *
 * Firebase Hosting drops the old hashed chunk files on deploy and the SPA rewrite
 * serves index.html in their place, so a tab left open across a release hits
 * ChunkLoadError the moment it navigates. Recovery is to reload once and pick up
 * the new release; a sessionStorage flag is what stops that becoming a loop.
 *
 * This app has no code splitting at all today: the workspace is the first route
 * tree to use it, because it adds a second design system and ~40 components that
 * no Appli-level page has any reason to download.
 */

const RELOAD_FLAG = 'chunk-error-reloaded';

const hasReloaded = () => {
    try {
        return sessionStorage.getItem(RELOAD_FLAG) === '1';
    } catch {
        // No sessionStorage means no way to remember a reload, so treat every
        // failure as the second one and let the boundary render.
        return true;
    }
};

const markReloaded = () => {
    try {
        sessionStorage.setItem(RELOAD_FLAG, '1');
    } catch {
        /* Unavailable — fall through to the error boundary. */
    }
};

export const clearReloadFlag = () => {
    try {
        sessionStorage.removeItem(RELOAD_FLAG);
    } catch {
        /* Nothing to clear. */
    }
};

export const isChunkLoadError = (error) => {
    if (!error) return false;
    if (error.name === 'ChunkLoadError') return true;
    return /loading (css )?chunk .* failed/i.test(error.message || '');
};

/** Reloads once for a chunk error; returns true when the reload was triggered. */
export const reloadOnceForChunkError = (error) => {
    if (!isChunkLoadError(error) || hasReloaded()) return false;
    markReloaded();
    window.location.reload();
    return true;
};

export const lazyWithReload = (importFn) =>
    lazy(() =>
        importFn().catch((error) => {
            if (reloadOnceForChunkError(error)) {
                // The page is reloading; keep Suspense on its fallback rather than
                // flashing an error the user will never have time to read.
                return new Promise(() => {});
            }
            throw error;
        })
    );
